import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import TopBar from './TopBar';
import ControllerWidget from './ControllerWidget';
import MainContent from './MainContent';
import Dashboard from './Dashboard';
import IdoraforditasokContent from './IdoraforditasokContent';
import FolyamatlapContent from './FolyamatlapContent';
import SzabadsagokContent from './SzabadsagokContent';
import CegesAutoContent from './CegesAutoContent';
import TaskManagerPage from './TaskManagerPageBackend';
import ProjekttervezesContent from './ProjekttervezesContent';
import AddMegrendeloDialog from './AddMegrendeloDialog';
import { ProjekttervezesFocus } from '../services/projekttervezesService';
import AddMunkaszamDialog from './AddMunkaszamDialog';
import AddIktatosDialog from './AddIktatosDialog';
import AddAjanlatDialog from './AddAjanlatDialog';
import { Permissions } from '../services/permissionService';

interface RightPanelProps {
  selectedMenu: string | null;
  onAuthClick: () => void;
  selectedMunkaszam?: string | null;
  selectedParticipant?: string | null;
  selectedFolyamatlapMunkaszam?: string | null;
  onMunkaszamCleared?: () => void;
  onNavigateToIdoraforditas?: (munkaszam: string) => void;
  onNavigateToParticipantTimeTracking?: (participantName: string, munkaszam: string) => void;
  onNavigateToFolyamatlap?: (munkaszam: string) => void;
  onBackToMunkaszamokFromFolyamatlap?: () => void;
  projekttervezesFocus?: ProjekttervezesFocus | null;
  onNavigateToProjekttervezesFeladat?: (focus: ProjekttervezesFocus) => void;
  onProjekttervezesFocusHandled?: () => void;
}

const RightPanel: React.FC<RightPanelProps> = ({ 
  selectedMenu, 
  onAuthClick, 
  selectedMunkaszam,
  selectedParticipant, 
  selectedFolyamatlapMunkaszam,
  onMunkaszamCleared,
  onNavigateToIdoraforditas,
  onNavigateToParticipantTimeTracking,
  onNavigateToFolyamatlap,
  onBackToMunkaszamokFromFolyamatlap,
  projekttervezesFocus,
  onNavigateToProjekttervezesFeladat,
  onProjekttervezesFocusHandled,
}) => {
  const [searchFilter, setSearchFilter] = useState<{column: string, value: string} | null>(null);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[] | undefined>(undefined);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [allData, setAllData] = useState<any[]>([]);

  // Jogosultságok mapping a selectedMenu alapján
  const getPermissionsForMenu = () => {
    switch (selectedMenu) {
      case 'ajanlatok':
        return {
          create: Permissions.CreateOffers,
          edit: Permissions.EditOffers,
          delete: Permissions.DeleteOffers,
        };
      case 'idoraforditasok':
        return {
          create: Permissions.CreateTimeTracking,
          edit: Permissions.EditTimeTracking,
          delete: Permissions.DeleteTimeTracking,
        };
      case 'iktatas':
        return {
          create: Permissions.CreateRegistrations,
          edit: Permissions.EditRegistrations,
          delete: Permissions.DeleteRegistrations,
        };
      case 'megrendelok':
        return {
          create: Permissions.CreateClients,
          edit: Permissions.EditClients,
          delete: Permissions.DeleteClients,
        };
      case 'munkaszamok':
        return {
          create: Permissions.CreateWorkNumbers,
          edit: Permissions.EditWorkNumbers,
          delete: Permissions.DeleteWorkNumbers,
        };
      case 'szabadsagok':
        return {
          create: Permissions.CreateLeaves,
          edit: Permissions.EditLeaves,
          delete: Permissions.DeleteLeaves,
        };
      case 'cegesauto':
        return {
          create: Permissions.CreateCompanyCar,
          edit: Permissions.EditCompanyCar,
          delete: Permissions.DeleteCompanyCar,
        };
      case 'projekttervezes':
        return {
          create: Permissions.CreateProjectPlanning,
          edit: Permissions.EditProjectPlanning,
          delete: Permissions.DeleteProjectPlanning,
        };
      default:
        return { create: undefined, edit: undefined, delete: undefined };
    }
  };

  const permissions = getPermissionsForMenu();

  // Reset states when menu changes
  React.useEffect(() => {
    setSearchFilter(null);
    setTableColumns([]);
    setVisibleColumns(undefined);
    // Ha elhagyjuk az időráfordítás menüt, töröljük a munkaszám kijelölést
    if (selectedMenu !== 'idoraforditasok' && onMunkaszamCleared) {
      onMunkaszamCleared();
    }
  }, [selectedMenu, onMunkaszamCleared]);

  const handleSearch = (column: string, searchValue: string) => {
    if (searchValue.trim()) {
      setSearchFilter({ column, value: searchValue });
    } else {
      setSearchFilter(null);
    }
  };

  const handleColumnsUpdate = (columns: string[]) => {
    setTableColumns(columns);
  };

  const handleDataUpdate = (data: any[]) => {
    setAllData(data);
  };

  const handleGetSuggestions = useCallback(async (searchValue: string): Promise<string[]> => {
    
    if (!allData || allData.length === 0 || searchValue.length < 3) {
      return [];
    }

    const lowerSearchValue = searchValue.toLowerCase();
    const uniqueValues = new Set<string>();

    // Collect all unique values from all rows that match the search
    allData.forEach(item => {
      Object.values(item).forEach(value => {
        if (value != null) {
          const stringValue = value.toString();
          if (stringValue.toLowerCase().includes(lowerSearchValue)) {
            uniqueValues.add(stringValue);
          }
        }
      });
    });

    // Return top 10 suggestions sorted by relevance
    return Array.from(uniqueValues)
      .sort((a, b) => {
        // Prioritize matches that start with the search value
        const aStarts = a.toLowerCase().startsWith(lowerSearchValue);
        const bStarts = b.toLowerCase().startsWith(lowerSearchValue);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b);
      })
      .slice(0, 10);
  }, [allData]);

  const handleVisibleColumnsChange = (columns: string[]) => {
    setVisibleColumns(columns);
  };

  const handleAddRecord = () => {
    // Only open dialog for supported tables (currently Megrendelők, Munkaszámok, Iktatások and Ajánlatok)
    if (selectedMenu === 'megrendelok' || selectedMenu === 'munkaszamok' || selectedMenu === 'iktatas' || selectedMenu === 'ajanlatok') {
      setAddDialogOpen(true);
    }
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
  };

  const handleAddSuccess = () => {
    // Trigger a refresh of the main content by changing the refresh trigger
    setRefreshTrigger(prev => prev + 1);
  };
  return (
    <Box 
      sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        height: '100vh',
        minWidth: 0, // Prevents flex item from growing beyond container
        overflow: 'hidden', // Prevents content from overflowing
      }}
    >
      <TopBar onAuthClick={onAuthClick} />
      {selectedMenu === 'dashboard' ? (
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          display: 'flex', 
          flexDirection: 'column',
        }}>
          <Dashboard 
            onAuthClick={onAuthClick}
            onNavigateToIdoraforditas={onNavigateToIdoraforditas}
            onNavigateToProjekttervezesFeladat={onNavigateToProjekttervezesFeladat}
          />
        </Box>
      ) : selectedMenu === 'idoraforditasok' ? (
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
        }}>
          <IdoraforditasokContent 
            onAddRecord={handleAddRecord} 
            initialMunkaszam={selectedMunkaszam}
            initialParticipant={selectedParticipant}
          />
        </Box>
      ) : selectedMenu === 'folyamatlap' ? (
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
        }}>
          <FolyamatlapContent
            munkaszam={selectedFolyamatlapMunkaszam}
            onBack={onBackToMunkaszamokFromFolyamatlap}
          />
        </Box>
      ) : selectedMenu === 'szabadsagok' ? (
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
        }}>
          <SzabadsagokContent />
        </Box>
      ) : selectedMenu === 'cegesauto' ? (
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
        }}>
          <CegesAutoContent />
        </Box>
      ) : selectedMenu === 'taskmanager' ? (
        <TaskManagerPage />
      ) : selectedMenu === 'projekttervezes' ? (
        <Box sx={{
          flexGrow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
        }}>
          <ProjekttervezesContent
            createPermission={permissions.create}
            editPermission={permissions.edit}
            deletePermission={permissions.delete}
            focus={projekttervezesFocus}
            onFocusHandled={onProjekttervezesFocusHandled}
          />
        </Box>
      ) : (
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
          gap: 2, // This creates equal spacing between ControllerWidget and MainContent
        }}>
          <ControllerWidget 
            selectedMenu={selectedMenu}
            tableColumns={tableColumns}
            visibleColumns={visibleColumns}
            onSearch={handleSearch}
            onVisibleColumnsChange={handleVisibleColumnsChange}
            onAddRecord={handleAddRecord}
            onGetSuggestions={handleGetSuggestions}
            createPermission={permissions.create}
          />
          <MainContent 
            selectedMenu={selectedMenu}
            searchFilter={searchFilter}
            onColumnsUpdate={handleColumnsUpdate}
            visibleColumns={visibleColumns}
            refreshTrigger={refreshTrigger}
            onDataUpdate={handleDataUpdate}
            editPermission={permissions.edit}
            deletePermission={permissions.delete}
            onNavigateToParticipantTimeTracking={onNavigateToParticipantTimeTracking}
            onNavigateToFolyamatlap={onNavigateToFolyamatlap}
          />
        </Box>
      )}

      {/* Add Dialogs */}
      {selectedMenu === 'megrendelok' && (
        <AddMegrendeloDialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
          onSuccess={handleAddSuccess}
        />
      )}
      {selectedMenu === 'munkaszamok' && (
        <AddMunkaszamDialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
          onSuccess={handleAddSuccess}
        />
      )}
      {selectedMenu === 'iktatas' && (
        <AddIktatosDialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
          onSuccess={handleAddSuccess}
        />
      )}
      {selectedMenu === 'ajanlatok' && (
        <AddAjanlatDialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
          onSuccess={handleAddSuccess}
        />
      )}
    </Box>
  );
};

export default RightPanel;
